/**
 * My Play Total / summary RTP — one rule for every aggregated view.
 *
 * Total RTP = floor(sum($ Won) / sum($ Played) × 100) as a whole percent.
 * Never average per-session RTP percentages.
 * When total $ Played is 0 (or non-finite), return 0 — callers display "0%"
 * to match existing empty-state conventions (never NaN / Infinity).
 */

export type MyPlayRtpSession = {
  moneyWon?: number | null;
  moneyPlayed?: number | null;
  /** FE Games Played cards use snake_case. */
  money_won?: number | null;
  money_played?: number | null;
};

const finiteOrZero = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const sessionWon = (s: MyPlayRtpSession): number =>
  finiteOrZero(s.moneyWon ?? s.money_won);

const sessionPlayed = (s: MyPlayRtpSession): number =>
  finiteOrZero(s.moneyPlayed ?? s.money_played);

/**
 * Whole-percent Total RTP over the sessions in the current view, rounded DOWN.
 */
export function computeTotalRtpPercent(sessions: readonly MyPlayRtpSession[]): number {
  if (!sessions || sessions.length === 0) return 0;

  let won = 0;
  let played = 0;
  for (const s of sessions) {
    if (!s) continue;
    won += sessionWon(s);
    played += sessionPlayed(s);
  }

  if (!(played > 0) || !Number.isFinite(won) || !Number.isFinite(played)) {
    return 0;
  }

  const pct = Math.floor((won / played) * 100);
  return Number.isFinite(pct) ? pct : 0;
}

/** Display helper: always a safe `"N%"` string (never NaN% / Infinity%). */
export function formatTotalRtpPercent(percent: number): string {
  if (!Number.isFinite(percent)) return '0%';
  return `${percent}%`;
}

/** Convenience: compute + format for a view's session list. */
export function totalRtpLabel(sessions: readonly MyPlayRtpSession[]): string {
  return formatTotalRtpPercent(computeTotalRtpPercent(sessions));
}
